import { Eye, EyeOff, UploadCloud, CheckCircle, CalendarDays, Check, XCircle } from 'lucide-react';
import React, { useState, useEffect, ChangeEvent, ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../api/axiosInstance";
import '../styles/RestaurantOnboarding.css';
// import OnboardingIllustration from '../assests/in-no-time-animate.svg';
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import {showGlobalToast} from '../utils/toast';

const logo = '/Dinelyt_Transparent.png';
// Type definitions for API responses
interface PincodeApiResponse {
  Status: string;
  PostOffice: {
    District: string;
    State: string;
  }[];
}

interface UpiValidationResponse {
  isValid: boolean;
  name?: string;
}

// Reusable Input Component with improved icon handling
interface CustomInputProps {
  id: string;
  label: string;
  name: string;
  value: string;
  onChange: (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => void;
  type?: string;
  placeholder?: string;
  readOnly?: boolean;
  className?: string;
  icon?: ReactNode; // Right-side icon
  leftIcon?: ReactNode; // Left-side icon for date/time pickers
  autoComplete?: string;
  maxLength?: number;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  customClassName?: string;
}

const CustomInput: React.FC<CustomInputProps> = ({ id, label, icon, leftIcon, customClassName, ...props }) => (
  <div className="space-y-2">
    <label htmlFor={id} className="block text-sm font-medium text-gray-300">
      {label}
    </label>
    <div className={`relative flex items-center bg-neutral-800 border border-neutral-700 rounded-xl focus-within:ring-2 focus-within:ring-orange-400 transition-all duration-200 ${customClassName}`}>
      {leftIcon && (
        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400">
          {leftIcon}
        </span>
      )}
      <input
        id={id}
        {...props}
        className={`w-full p-3 bg-transparent outline-none placeholder-gray-500 text-white ${props.readOnly ? 'cursor-not-allowed bg-neutral-700' : ''} ${leftIcon ? 'pl-10' : 'pl-3'} ${icon ? 'pr-10' : 'pr-3'} ${props.className || ''}`}
        style={['date', 'time'].includes(props.type || '') ? { appearance: 'none', WebkitAppearance: 'none' } : {}}
      />
      {icon && (
        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
          {icon}
        </span>
      )}
    </div>
  </div>
);

interface DatePickerInputProps {
  label: string;
  name: string;
  value: Date | null; // store as Date in form
  onChange: (date: Date | null) => void; // pass Date to form
}

const DatePickerInput: React.FC<DatePickerInputProps> = ({ label, name, value, onChange }) => {
  return (
    <DatePicker
      selected={value}
      onChange={onChange}
      dateFormat="yyyy-MM-dd"
      customInput={
        <CustomInput
          id={name}
          label={label}
          name={name}
          value={value ? value.toISOString().split("T")[0] : ""}
          onChange={() => { }}
          icon={<CalendarDays size={20} className="text-gray-400 dark:text-gray-200" />}
          type="text"
        />
      }
    />
  );
};

// Reusable File Uploader Component
interface FileUploaderProps {
  label: string;
  fileName: string | null;
  name: string;
  onChange: (e: ChangeEvent<HTMLInputElement>) => void;
}

const FileUploader: React.FC<FileUploaderProps> = ({ label, fileName, name, onChange }) => (
  <div className="space-y-2">
    <label className="block text-sm font-medium text-gray-300">{label}</label>
    <div className="flex items-center justify-between border border-neutral-700 rounded-xl p-3 bg-neutral-800 transition-all duration-200">
      <div className="flex items-center space-x-2 overflow-hidden text-ellipsis">
        <UploadCloud size={20} className="text-gray-500 flex-shrink-0" />
        <span className="text-gray-400 overflow-hidden text-ellipsis whitespace-nowrap">
          {fileName ? fileName : 'Upload file'}
        </span>
      </div>
      <label className="bg-orange-500 hover:bg-orange-600 text-white py-2 px-4 rounded-full text-sm font-medium cursor-pointer transition-colors duration-200">
        Browse
        <input
          type="file"
          name={name}
          onChange={onChange}
          className="hidden"
        />
      </label>
    </div>
  </div>
);

const RestaurantOnboarding: React.FC = () => {
  const [step, setStep] = useState(1);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState<string[]>(["", "", "", ""]);
  const [form, setForm] = useState({
    emailOrMobile: "",
    restaurantName: "",
    streetAddress: "",
    city: "",
    state: "",
    pincode: "",
    mobile: "",
    email: "",
    openTime: "",
    closeTime: "",
    gstNumber: "",
    gstDate: null as Date | null,
    fssaiNumber: "",
    fssaiValidity: null as Date | null,
    gstFile: null as File | null,
    fssaiFile: null as File | null,
    ownerName: "",
    password: "",
    confirmPassword: "",
    logoFile: null as File | null,
    upiId: "",
  });

  const [passwordError, setPasswordError] = useState<string>("");
  const [upiStatus, setUpiStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [upiName, setUpiName] = useState<string>('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [timer, setTimer] = useState(30);
  const [isMobileInput, setIsMobileInput] = useState(true);
  const navigate = useNavigate();
  const [passwordChecking, setPasswordChecking] = useState<boolean>(false);
  const [backgroundImage, setBackgroundImage] = useState('');

  // Helper: browser SHA-1 hash to hex
  async function sha1Hex(str: string): Promise<string> {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await window.crypto.subtle.digest('SHA-1', data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, '0')).join('').toUpperCase();
  }

  // Random image for background
  useEffect(() => {
    const images = [
      'https://images.unsplash.com/photo-1542435503-956c469947f6?ixlib=rb-4.0.3&q=85&fm=jpg&crop=entropy&cs=srgb&w=1920'
    ];
    const randomIndex = Math.floor(Math.random() * images.length);
    setBackgroundImage(images[randomIndex]);
  }, []);

  // Password exposure check
  const checkPasswordStrengthAndExposure = async (password: string) => {
    if (password.length < 8) {
      setPasswordError("Password must be at least 8 characters long.");
      return false;
    }
    if (!/[A-Z]/.test(password) || !/[a-z]/.test(password) || !/[0-9]/.test(password) || !/[^A-Za-z0-9]/.test(password)) {
      setPasswordError("Password should include uppercase, lowercase, number, and special character.");
      return false;
    }
    setPasswordError("");
    setPasswordChecking(true);
    const sha1 = await sha1Hex(password);
    const prefix = sha1.slice(0, 5);
    const suffix = sha1.slice(5);
    try {
      const res = await fetch(`https://api.pwnedpasswords.com/range/${prefix}`);
      const text = await res.text();
      if (text.toUpperCase().includes(suffix)) {
        setPasswordError("This password has been exposed in a data breach. Please choose another strong password.");
        setPasswordChecking(false);
        return false;
      }
    } catch (e) {
      console.error("Failed to check password exposure:", e);
    }
    setPasswordChecking(false);
    return true;
  };

  // UPI ID validation
  const validateUpiId = async (upiId: string) => {
    setUpiStatus('validating');
    setUpiName('');
    const upiRegex = /^[\w.-]{3,}@[\w.-]{2,}$/;
    if (!upiRegex.test(upiId)) {
      setUpiStatus('invalid');
      return;
    }

    try {
      // Mock API call for UPI validation
      const response = await axiosInstance.post<UpiValidationResponse>("/Auth/ValidateUpi", { upiId });
      if (response.data.isValid) {
        setUpiStatus('valid');
        setUpiName(response.data.name || 'UPI User');
      } else {
        setUpiStatus('invalid');
      }
    } catch (err) {
      console.error("UPI validation failed:", err);
      setUpiStatus('invalid');
    }
  };

  // Pre-populate mobile/email in Step 2 based on identifier
  useEffect(() => {
    if (step === 2) {
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (emailRegex.test(form.emailOrMobile)) {
        setForm((prev) => ({ ...prev, email: prev.emailOrMobile, mobile: "" }));
      } else {
        setForm((prev) => ({ ...prev, mobile: prev.emailOrMobile, email: "" }));
      }
    }
  }, [step, form.emailOrMobile]);

  // Handle OTP input change
  const handleOtpChange = (e: ChangeEvent<HTMLInputElement>, index: number) => {
    const value = e.target.value;
    if (/^[0-9]$/.test(value) || value === "") {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);
      if (value && index < 3) {
        document.getElementById(`otp-input-${index + 1}`)?.focus();
      }
    }
  };

  // Handle general form input change
  const handleChange = async (e: ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, files } = e.target as HTMLInputElement;

    if (files) {
      setForm((prev) => ({ ...prev, [name]: files[0] }));
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
      if (name === "emailOrMobile") {
        setIsMobileInput(!value.includes('@') && value !== '');
      }
      if (name === "password") {
        checkPasswordStrengthAndExposure(value);
      }
      if (name === "confirmPassword") {
        if (value && form.password !== value) {
          setPasswordError('Passwords do not match.');
        } else {
          setPasswordError('');
        }
      }
      if (name === "upiId") {
        if (value.length > 5) {
          validateUpiId(value);
        } else {
          setUpiStatus('idle');
          setUpiName('');
        }
      }
    }
  };

  // Auto-fetch City and State based on Pincode
  useEffect(() => {
    const fetchLocation = async () => {
      if (form.pincode.length === 6 && !isNaN(Number(form.pincode))) {
        try {
          setIsLoading(true);
          const response = await fetch(`https://api.postalpincode.in/pincode/${form.pincode}`);
          const data: PincodeApiResponse[] = await response.json();

          if (data[0]?.Status === "Success" && data[0]?.PostOffice?.length > 0) {
            const { District, State } = data[0].PostOffice[0];
            setForm((prev) => ({ ...prev, city: District, state: State }));
            setError("");
          } else {
            setError("Invalid pincode. Please enter a valid 6-digit Indian pincode.");
            setForm((prev) => ({ ...prev, city: "", state: "" }));
          }
        } catch (err) {
          setError("Failed to fetch location. Please enter manually.");
          setForm((prev) => ({ ...prev, city: "", state: "" }));
        } finally {
          setIsLoading(false);
        }
      } else if (form.pincode.length < 6) {
        setForm((prev) => ({ ...prev, city: "", state: "" }));
        setError("");
      }
    };
    fetchLocation();
  }, [form.pincode]);

  // OTP Timer Logic
  useEffect(() => {
    let interval: NodeJS.Timeout | null = null;
    if (otpSent && timer > 0) {
      interval = setInterval(() => {
        setTimer((prev) => prev - 1);
      }, 1000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [otpSent, timer]);

  const validateIdentifier = () => {
    const { emailOrMobile } = form;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const mobileRegex = /^[0-9]{10}$/;

    if (mobileRegex.test(emailOrMobile)) {
      setIsMobileInput(true);
      return true;
    }
    if (emailRegex.test(emailOrMobile)) {
      setIsMobileInput(false);
      return true;
    }
    setError("Please enter a valid 10-digit mobile number or a valid email address.");
    return false;
  };

  const handleSendOtp = async () => {
    if (!validateIdentifier()) return;
    setError("");
    setIsLoading(true);
    try {
      await axiosInstance.post("/Auth/send-otp", { identifier: form.emailOrMobile });
      setOtpSent(true);
      setTimer(30);
      showGlobalToast("OTP sent successfully!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to send OTP. Please try again.");
      showGlobalToast(err.response?.data?.message || "Failed to send OTP.", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendOtp = async () => {
    setError("");
    setIsLoading(true);
    try {
      await axiosInstance.post("/Auth/send-otp", {
        identifier: form.emailOrMobile,
      });
      setTimer(30); // Reset timer
      setOtp(new Array(4).fill("")); // Clear OTP input
      showGlobalToast("OTP resent successfully!");
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to resend OTP.");
      showGlobalToast(err.response?.data?.message || "Failed to resend OTP.", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    setError("");
    setIsLoading(true);
    try {
      const fullOtp = otp.join("");
      const res = await axiosInstance.post("/auth/verify-otp", {
        identifier: form.emailOrMobile,
        otp: fullOtp,
      });
      if (res.data?.isVerified) {
        setStep(2);
      } else {
        setError("Invalid OTP. Please try again.");
        showGlobalToast("Invalid OTP. Please try again.", 'error');
      }
    } catch (err: any) {
      setError(err.response?.data?.message || "OTP verification failed.");
      showGlobalToast(err.response?.data?.message || "OTP verification failed.", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle pasting into the first OTP input
  const handleOtpPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pasteData = e.clipboardData.getData('text').trim();
    if (/^\d{4}$/.test(pasteData)) {
      setOtp(pasteData.split(""));
    }
  };

  const handleSaveAllDetails = async () => {
    setError("");
    setIsLoading(true);

    if (form.password !== form.confirmPassword) {
      showGlobalToast("Passwords do not match.", 'error');
      setIsLoading(false);
      return;
    }

    const isPasswordValid = await checkPasswordStrengthAndExposure(form.password);
    if (!isPasswordValid) {
      showGlobalToast(passwordError || "Password is not strong enough.", 'error');
      setIsLoading(false);
      return;
    }

    // Check UPI validity before submission
    if (upiStatus !== 'valid') {
      showGlobalToast('Please enter a valid UPI ID.', 'error');
      setIsLoading(false);
      return;
    }

    const formData = new FormData();
    formData.append("email", form.email);
    formData.append("mobile", form.mobile);
    formData.append("name", form.restaurantName);
    formData.append("address", form.streetAddress);
    formData.append("city", form.city);
    formData.append("state", form.state);
    formData.append("pincode", form.pincode);
    formData.append("openTime", form.openTime);
    formData.append("closeTime", form.closeTime);
    formData.append("GSTIN", form.gstNumber);
    formData.append("GSTINRegDate", form.gstDate ? form.gstDate.toISOString().split("T")[0] : "");
    formData.append("FSSAINo", form.fssaiNumber);
    formData.append("FSSAIValidUpto", form.fssaiValidity ? form.fssaiValidity.toISOString().split("T")[0] : "");
    formData.append("ownerName", form.ownerName);
    formData.append("password", form.password);
    formData.append("upiId", form.upiId);

    if (form.gstFile) formData.append("gstinFile", form.gstFile);
    if (form.fssaiFile) formData.append("fssaiFile", form.fssaiFile);
    if (form.logoFile) formData.append("logo", form.logoFile);

    try {
      await axiosInstance.post("/restaurant/register", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });

      showGlobalToast("Restaurant registered successfully! Redirecting to login.");
      setTimeout(() => { navigate("/admin/login"); }, 2000);
    } catch (err: any) {
      setError(err.response?.data?.message || "Failed to save details. Please check all fields.");
      showGlobalToast(err.response?.data?.message || "Failed to register. Please check your details.", 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const renderStep = () => {
    switch (step) {
      case 1:
        return (
          <div className="text-center">
            <h2 className="text-2xl md:text-3xl font-bold mb-2 text-white">Welcome to Dinelyt👋</h2>
            <p className="text-sm md:text-base text-gray-400 mb-8">
              Simplify your restaurant operations and grow your business. <br />
              Unlock seamless billing, table management and online orders
            </p>

            {!otpSent ? (
              <>
                <label htmlFor="emailOrMobile" className="block text-left text-sm font-medium text-gray-300 mb-2">
                  Mobile Number or Email
                </label>
                <div className="flex items-center space-x-2 border border-gray-600 rounded-lg p-3 focus-within:border-orange-400 transition-colors mb-6">
                  {isMobileInput && (
                    <span className="text-gray-400 pr-2 border-r border-gray-600 flex items-center">
                      +91 <span className="ml-2">🇮🇳</span>
                    </span>
                  )}
                  <input
                    id="emailOrMobile"
                    type="text"
                    name="emailOrMobile"
                    placeholder="Enter mobile or email"
                    value={form.emailOrMobile}
                    onChange={handleChange}
                    className="w-full bg-transparent outline-none placeholder-gray-500 text-white"
                  />
                </div>
                <button
                  onClick={handleSendOtp}
                  disabled={isLoading || !form.emailOrMobile}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 px-4 rounded-lg transition-colors disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Sending...' : 'Send OTP'}
                </button>
              </>
            ) : (
              <>
                <p className="text-gray-400 mb-4 text-center">
                  Enter OTP sent to your {isMobileInput ? 'mobile' : 'email'}
                </p>
                <div className="flex justify-center space-x-2 mb-4">
                  {otp.map((digit, index) => (
                    <input
                      key={index}
                      id={`otp-input-${index}`}
                      type="text"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleOtpChange(e, index)}
                      onPaste={index === 0 ? handleOtpPaste : undefined}
                      onKeyDown={(e) => {
                        if (e.key === "Backspace" && !otp[index] && index > 0) {
                          document.getElementById(`otp-input-${index - 1}`)?.focus();
                        }
                      }}
                      className="w-12 h-12 text-center text-xl bg-neutral-800 border border-gray-600 rounded-lg focus:outline-none focus:border-orange-400 transition-colors text-white"
                    />
                  ))}
                </div>
                <div className="flex justify-between items-center mb-6 text-sm">
                  <span className="text-gray-400">
                    Resend OTP in <span className="text-orange-400">{timer}s</span>
                  </span>
                  <button
                    onClick={handleResendOtp}
                    disabled={isLoading || timer > 0}
                    className="text-orange-400 hover:underline disabled:text-gray-500 disabled:cursor-not-allowed"
                  >
                    Resend
                  </button>
                </div>
                <button
                  onClick={handleVerifyOtp}
                  disabled={isLoading || otp.join("").length < 4}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold py-3 rounded-lg transition-colors disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed"
                >
                  {isLoading ? 'Verifying...' : 'Verify OTP'}
                </button>
                <div className="text-center mt-4">
                  <button
                    onClick={() => setOtpSent(false)}
                    className="text-sm text-gray-400 hover:underline"
                  >
                    Is this {form.emailOrMobile} correct? Click to edit
                  </button>
                </div>
              </>
            )}
            {/* <p className="text-center text-xs text-gray-400 mt-6">
              Complete signup to get personalized assistance from our sales team.
            </p> */}
            <p className="text-center text-sm text-gray-400 mt-6">
              Already <span className="text-orange-400 font-medium">Dinelyting</span> with us?{" "}
              <button
                onClick={() => navigate("/restaurant/login")}
                className="text-orange-400 font-medium hover:underline ml-1"
              >
                Continue
              </button>
            </p>
          </div>
        );

      case 2:
        return (
          <div className="space-y-8">
            <div className="text-center">
              <h2 className="text-2xl md:text-3xl font-bold text-white">Restaurant Details</h2>
              <p className="text-sm md:text-base text-gray-400 mt-2">
                Provide your restaurant and owner information.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <CustomInput
                id="restaurantName"
                label="Restaurant Name"
                name="restaurantName"
                placeholder="e.g., The Grand Kitchen"
                value={form.restaurantName}
                onChange={handleChange}
              />
              <CustomInput
                id="ownerName"
                label="Owner's Full Name"
                name="ownerName"
                placeholder="e.g., Jane Doe"
                value={form.ownerName}
                onChange={handleChange}
              />
              <CustomInput
                id="mobile"
                label="Mobile Number"
                name="mobile"
                placeholder="Mobile number"
                value={form.mobile}
                onChange={handleChange}
                readOnly={!!form.emailOrMobile && isMobileInput}
              />
              <CustomInput
                id="email"
                label="Email"
                name="email"
                type="email"
                placeholder="Email address"
                value={form.email}
                onChange={handleChange}
                readOnly={!!form.emailOrMobile && !isMobileInput}
              />
              <div className="md:col-span-2">
                <CustomInput
                  id="password"
                  label="Set Password"
                  name="password"
                  type={showPassword ? "text" : "password"}
                  placeholder="Set a strong password"
                  value={form.password}
                  onChange={handleChange}
                  autoComplete="new-password"
                  icon={
                    <button type="button" className="text-gray-400 hover:text-orange-400 transition-colors" tabIndex={-1} onClick={() => setShowPassword((prev) => !prev)}>
                      {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                    </button>
                  }
                />
                {passwordError && (
                  <p className="text-red-400 text-xs mt-2">{passwordError}</p>
                )}
                {passwordChecking && !passwordError && (
                  <p className="text-orange-400 text-xs mt-2">Checking password safety...</p>
                )}
              </div>
              <CustomInput
                id="confirmPassword"
                label="Confirm Password"
                name="confirmPassword"
                type="password"
                placeholder="Re-enter password"
                value={form.confirmPassword}
                onChange={handleChange}
                autoComplete="new-password"
                onBlur={() => {
                  if (form.confirmPassword && form.password !== form.confirmPassword) {
                    setPasswordError('Passwords do not match.');
                  }
                }}
              />
              <div className="md:col-span-2">
                <CustomInput
                  id="streetAddress"
                  label="Street Address"
                  name="streetAddress"
                  placeholder="Enter your street address"
                  value={form.streetAddress}
                  onChange={handleChange}
                />
              </div>
              <CustomInput
                id="pincode"
                label="Pincode"
                name="pincode"
                placeholder="e.g., 560001"
                value={form.pincode}
                onChange={handleChange}
                maxLength={6}
              />
              <CustomInput
                id="city"
                label="City"
                name="city"
                placeholder="City"
                value={form.city}
                onChange={handleChange}
              //readOnly={!!form.city && !!form.state && form.pincode.length === 6}
              />
              <CustomInput
                id="state"
                label="State"
                name="state"
                placeholder="State"
                value={form.state}
                onChange={handleChange}
              //readOnly={!!form.city && !!form.state && form.pincode.length === 6}
              />

              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-300 mb-2">Operating Hours</label>
                <div className="flex items-center gap-4">
                  <CustomInput
                    id="openTime"
                    label=""
                    name="openTime"
                    type="time"
                    value={form.openTime}
                    onChange={handleChange}
                    className="flex-1"
                  />
                  <span className="text-gray-400 text-lg">-</span>
                  <CustomInput
                    id="closeTime"
                    label=""
                    name="closeTime"
                    type="time"
                    value={form.closeTime}
                    onChange={handleChange}
                    className="flex-1"
                  />
                </div>
              </div>
            </div>

            <hr className="border-t border-neutral-700 my-8" />

            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white">Regulatory Information</h3>
              <CustomInput
                id="gstNumber"
                label="GST Registration Number"
                name="gstNumber"
                placeholder="Enter GSTIN"
                value={form.gstNumber}
                onChange={handleChange}
              />
              <DatePickerInput
                label="GST Registration Date"
                name="gstDate"
                value={form.gstDate} // Date | null
                onChange={(date) => setForm({ ...form, gstDate: date })}
              />
              <FileUploader
                label="Upload GST Certificate"
                name="gstFile"
                fileName={form.gstFile?.name || null}
                onChange={handleChange}
              />
              <CustomInput
                id="fssaiNumber"
                label="FSSAI License Number"
                name="fssaiNumber"
                placeholder="Enter FSSAI number"
                value={form.fssaiNumber}
                onChange={handleChange}
              />
              {/* <CustomInput
                id="fssaiValidity"
                label="FSSAI License Validity"
                name="fssaiValidity"
                type="date"
                value={form.fssaiValidity}
                onChange={handleChange}
                leftIcon={<CalendarDays size={20} />}
              /> */}
              <DatePickerInput
                label="FSSAI License Validity"
                name="fssaiValidity"
                value={form.fssaiValidity} // Date | null
                onChange={(date) => setForm({ ...form, fssaiValidity: date })}
              />
              <FileUploader
                label="Upload FSSAI Certificate"
                name="fssaiFile"
                fileName={form.fssaiFile?.name || null}
                onChange={handleChange}
              />
            </div>

            <hr className="border-t border-neutral-700 my-8" />

            <div className="space-y-6">
              <h3 className="text-xl font-bold text-white">Payment Information</h3>
              <div className="relative">
                <CustomInput
                  id="upiId"
                  label="UPI ID"
                  name="upiId"
                  placeholder="e.g., yourname@bank"
                  value={form.upiId}
                  onChange={handleChange}
                />
                <span className="absolute right-3 top-10 transform -translate-y-1/2 flex items-center">
                  {upiStatus === 'validating' && <span className="animate-spin h-5 w-5 border-2 border-t-2 border-orange-400 rounded-full"></span>}
                  {upiStatus === 'valid' && <Check size={20} className="text-green-500" />}
                  {upiStatus === 'invalid' && <XCircle size={20} className="text-red-500" />}
                </span>
              </div>
              {upiStatus === 'valid' && (
                <CustomInput
                  id="upiName"
                  label="Name"
                  name="upiName"
                  value={upiName}
                  onChange={() => { }} // Read-only field, no onChange
                  readOnly={true}
                  customClassName="mt-2"
                />
              )}
            </div>

            <hr className="border-t border-neutral-700 my-8" />

            <FileUploader
              label="Restaurant Logo"
              name="logoFile"
              fileName={form.logoFile?.name || null}
              onChange={handleChange}
            />

            <div className="flex justify-between mt-8">
              <button
                onClick={() => setStep(1)}
                className="px-8 py-3 border border-neutral-700 rounded-xl text-gray-300 hover:bg-neutral-800 transition-colors duration-200"
              >
                Back
              </button>
              <button
                onClick={handleSaveAllDetails}
                disabled={isLoading}
                className="px-8 py-3 bg-orange-500 hover:bg-orange-600 text-white rounded-xl transition-colors duration-200 disabled:bg-gray-700 disabled:text-gray-400 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
              >
                {isLoading ? 'Submitting...' : 'Submit'}
              </button>
            </div>
          </div>
        );
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4 md:p-8 bg-neutral-950 font-sans antialiased text-gray-300 relative overflow-hidden">
      <div
        className="absolute inset-0 z-0 bg-cover bg-center"
        style={{
          backgroundImage: `url('${backgroundImage}')`,
          filter: 'blur(8px)',
          transform: 'scale(1.05)',
        }}
      />
      <div className="absolute inset-0 z-0 bg-neutral-950 opacity-70" />

      <div className="w-full max-w-4xl p-6 md:p-12 rounded-3xl relative z-10 flex flex-col md:flex-row shadow-2xl bg-neutral-900 overflow-hidden">
        <div className="flex-1 p-2 md:p-2 space-y-2">
          <div className="flex flex-col sm:flex-row items-center sm:justify-between mb-8 w-full">
            {/* Logo */}
            <img
              src={logo}  //"http://192.168.0.191:44381/uploads/Dinelyt_Transparent.png"
              alt="Dinelyt Logo"
              className="w-3/4 sm:w-1/2 lg:w-1/3 max-w-xs sm:max-w-md lg:max-w-lg h-auto mb-3 sm:mb-0"
            />

            {/* Title */}
            <h1 className="text-lg sm:text-2xl md:text-3xl lg:text-4xl font-bold text-white text-center sm:text-left px-2">
              Restaurant Onboarding
            </h1>
          </div>

          <div className="flex items-center justify-between mb-12">
            <div className={`relative flex items-center ${step === 1 ? 'text-orange-400' : 'text-gray-500'}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${step >= 1 ? 'border-orange-400' : 'border-gray-500'} transition-colors duration-300`}>
                <span className="font-bold">{step > 1 ? <CheckCircle size={20} className="text-green-500" /> : '1'}</span>
              </div>
              <span className="ml-3 text-sm font-medium hidden md:block">Verification</span>
            </div>
            <div className={`flex-1 h-0.5 mx-4 ${step === 2 ? 'bg-orange-400' : 'bg-gray-500'} transition-colors duration-300`} />
            <div className={`relative flex items-center ${step === 2 ? 'text-orange-400' : 'text-gray-500'}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-full border-2 ${step === 2 ? 'border-orange-400' : 'border-gray-500'} transition-colors duration-300`}>
                <span className="font-bold">2</span>
              </div>
              <span className="ml-3 text-sm font-medium hidden md:block">Details</span>
            </div>
          </div>
          {error && <p className="text-red-400 text-center mb-6">{error}</p>}
          {renderStep()}
        </div>
        {/* <div className="hidden md:flex flex-1 items-center justify-center p-8 relative rounded-2xl">
          <img
            //src={OnboardingIllustration} 
            src="/in-no-time-animate.svg"
            alt="Onboarding graphic"
            className="w-full h-auto object-contain rounded-2xl"
          />
        </div> */}
      </div>
      {isLoading && (
        <div className="absolute inset-0 bg-neutral-900 bg-opacity-80 flex flex-col items-center justify-center rounded-2xl z-20">
          <div className="loader ease-linear rounded-full border-4 border-t-4 border-orange-400 h-12 w-12 mb-4 animate-spin-slow"></div>
          <p className="text-white text-lg">Loading...</p>
        </div>
      )}
    </div>
  );
};

export default RestaurantOnboarding;